import React, { useState, useRef } from 'react';
import { Button, Input, InfoBlock, StatusChip, Table, Text, Accordion, Popper } from './index';

import { SearchRounded, InfoRounded, Settings, Save, CheckCircleRounded, AddRounded, ErrorRounded } from '@mui/icons-material';

const propTableColumns = [
    { title: 'Prop Name', field: 'name', width: 160, customRender: (row) => <Text.span weight={600} style={{ color: 'var(--primary, #0055ff)' }}>{row.name}</Text.span> },
    { title: 'Type', field: 'type', width: 240, customRender: (row) => <Text.span style={{ fontFamily: 'monospace', color: '#ff9800' }}>{row.type}</Text.span> },
    { title: 'Default', field: 'defaultVal', width: 140, customRender: (row) => <Text.span style={{ fontFamily: 'monospace', color: '#4caf50' }}>{row.defaultVal}</Text.span> },
    { title: 'Description', field: 'description', grow: 1, customRender: (row) => <Text.span contrast={6}>{row.description}</Text.span> }
];

const PropDocTable = ({ rows }) => (
    <div style={{ marginTop: 24 }}>
        <Table
            contained={true}
            draggable={false}
            columns={propTableColumns}
            data={rows}
        />
    </div>
);

const SectionHeader = ({ title, description }) => (
    <div style={{ marginBottom: 32 }}>
        <Text.h2>{title}</Text.h2>
        <Text.p contrast={6} style={{ marginTop: 8 }}>{description}</Text.p>
    </div>
);


export default function FragmentsExamples() {
    const [activeComponent, setActiveComponent] = useState('Button');

    // Accordion State
    const [openStep, setOpenStep] = useState('step1');
    const [completedSteps, setCompletedSteps] = useState({ step1: true });

    // Popper State
    const popperControlStandard = useRef(null);
    const popperControlSearch = useRef(null);
    const popperControlMulti = useRef(null);

    const handleOpenPopperStandard = (e) => {
        popperControlStandard.current?.open([
            { id: 1, label: 'Settings', icon: <Settings fontSize="small" />, onClick: () => alert('Settings clicked') },
            { id: 2, label: 'Profile', onClick: () => alert('Profile clicked') },
            { id: 4, label: 'Clear Cache', variant: 'warning', onClick: () => alert('Warning item') },
            { id: 3, label: 'Logout', disabled: true },
        ], 'bottom', e.currentTarget);
    };

    const handleOpenPopperSearch = (e) => {
        popperControlSearch.current?.open([
            { id: 1, label: 'Apple' },
            { id: 2, label: 'Banana' },
            { id: 3, label: 'Orange' },
            { id: 4, label: 'Strawberry' },
            { id: 5, label: 'Watermelon' },
        ], 'bottom', e.currentTarget);
    };

    const handleOpenPopperMulti = (e) => {
        popperControlMulti.current?.open([
            { id: 1, label: 'Option A', checked: true, onClick: () => alert('Toggled A') },
            { id: 2, label: 'Option B', checked: false, onClick: () => alert('Toggled B') },
            { id: 3, label: 'Option C', checked: false, onClick: () => alert('Toggled C') },
        ], 'bottom', e.currentTarget);
    };

    // Table State
    const sampleTableColumns = [
        { title: 'ID', field: 'id', width: 50 },
        { title: 'Name', field: 'name', grow: 1 },
        { title: 'Role', field: 'role' },
        { title: 'Status', render: (row) => <StatusChip variant={row.status === 'Active' ? 'success' : 'error'} text={row.status} /> }
    ];
    const sampleTableData = [
        { id: 1, name: 'Alice Cooper', role: 'Admin', status: 'Active' },
        { id: 2, name: 'Bob Dylan', role: 'Editor', status: 'Inactive' },
        { id: 3, name: 'Charlie Puth', role: 'Viewer', status: 'Active' },
    ];

    const componentsList = [
        'Button', 'Text', 'Input', 'StatusChip', 'InfoBlock', 'Table', 'Accordion', 'Popper', 'Confirm'
    ];

    const renderContent = () => {
        switch(activeComponent) {
            case 'Button':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Button Component" description="Highly customizable button with different variants, colors, and states." />
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button variant="default" color="primary">Primary</Button>
                                <Button variant="default" color="error">Error</Button>
                                <Button variant="default" color="success">Success</Button>
                                <Button variant="default" color="warning">Warning</Button>
                                <Button variant="default" color="inverse">Inverse</Button>
                                <Button variant="default" color="shiny">Shiny</Button>
                            </div>

                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button variant="dashed" color="primary">Dashed Button</Button>
                                <Button variant="text" color="primary">Text Button</Button>
                                <Button variant="rounded" color="primary">Rounded</Button>
                                <Button loading>Loading</Button>
                                <Button disabled>Disabled</Button>
                            </div>

                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button startIcon={<CheckCircleRounded />} color="primary">Start Icon</Button>
                                <Button endIcon={<AddRounded />} color="black">End Icon</Button>
                                <Button icon={<ErrorRounded />} color="error" />
                                <Button badge={3} color="primary">With Badge</Button>
                            </div>
                        </div>

                        <PropDocTable rows={[
                            { name: 'variant', type: '"default" | "rounded" | "text" | "dashed"', defaultVal: '"default"', description: 'The visual style of the button.' },
                            { name: 'color', type: '"white" | "black" | "primary" | "error" | "warning" | "info" | "success" | "inverse" | "shiny"', defaultVal: '"primary"', description: 'Color theme of the button.' },
                            { name: 'size', type: '"small" | "medium" | "large"', defaultVal: '"medium"', description: 'Dimensions of the button.' },
                            { name: 'loading', type: 'boolean', defaultVal: 'false', description: 'Shows a spinner and disables the button.' },
                            { name: 'disabled', type: 'boolean', defaultVal: 'false', description: 'Disables interactions and reduces opacity.' },
                            { name: 'fullWidth', type: 'boolean', defaultVal: 'false', description: 'Expands the button to 100% of its container.' },
                            { name: 'startIcon / endIcon', type: 'ReactNode', defaultVal: 'null', description: 'Icon elements to place before or after the label.' },
                            { name: 'icon', type: 'ReactNode', defaultVal: 'null', description: 'Icon for an icon-only button.' },
                            { name: 'badge', type: 'number', defaultVal: 'undefined', description: 'Displays a notification badge on the button.' },
                            { name: 'tooltip', type: 'string', defaultVal: 'undefined', description: 'Hover text tooltip.' },
                            { name: 'onClick', type: 'function', defaultVal: 'undefined', description: 'Click handler. Supports async operations to auto-show loading state.' }
                        ]} />
                    </div>
                );

            case 'Text':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Text Component" description="Comprehensive typography system containing everything from h1 to small tags." />
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                            <Text.h1>Heading 1 (h1)</Text.h1>
                            <Text.h2>Heading 2 (h2)</Text.h2>
                            <Text.h3 left={<InfoRounded />}>Heading 3 with Icon</Text.h3>
                            <Text.p contrast={8}>This is a standard paragraph component (p). Useful for long form texts and general descriptions. It has a high contrast.</Text.p>
                            <Text.span contrast={5}>Span with lower contrast (contrast=5)</Text.span>
                            <Text.small weight={600}>Small text but bold (weight=600)</Text.small>
                            <Text.p maxLines={1} style={{ width: 150 }}>This text is truncated using maxLines because it is very very very long.</Text.p>
                            <Text.h4 animated>Animated typing effect</Text.h4>
                        </div>

                        <PropDocTable rows={[
                            { name: 'contrast', type: 'number (0-10)', defaultVal: '10', description: 'Adjusts the opacity/contrast of the text color. 10 is fully visible.' },
                            { name: 'weight', type: 'number | string', defaultVal: 'null', description: 'CSS font-weight.' },
                            { name: 'maxLines', type: 'number', defaultVal: 'undefined', description: 'Truncates text with ellipsis after X lines.' },
                            { name: 'grow', type: 'boolean', defaultVal: 'false', description: 'Applies flex-grow: 1.' },
                            { name: 'loading', type: 'boolean', defaultVal: 'false', description: 'Renders skeleton loading background.' },
                            { name: 'animated', type: 'boolean', defaultVal: 'false', description: 'Types out the string character by character on mount.' },
                            { name: 'animatedCharDelay', type: 'number', defaultVal: '0.05', description: 'Delay between characters for animated text (seconds).' },
                            { name: 'left / right', type: 'ReactNode', defaultVal: 'null', description: 'Renders icons alongside the text, handling spacing via flex-gap automatically.' }
                        ]} />
                    </div>
                );

            case 'Input':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Input Components" description="A unified compound component grouping together various input forms: Text, Email, Switch, Phone, Integer, CPF, etc." />
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12, maxWidth: 600 }}>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.Text label="Text Input" placeholder="Type a regular text..." start={<SearchRounded fontSize="small" />} /></div>
                                <div style={{ flex: 1 }}><Input.Email label="Email Input" placeholder="email@example.com" /></div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.Integer label="Integer Input" placeholder="123" /></div>
                                <div style={{ flex: 1 }}><Input.Money label="Money Input" placeholder="0,00" /></div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.CPF label="CPF Input" placeholder="000.000.000-00" /></div>
                                <div style={{ flex: 1 }}><Input.CNPJ label="CNPJ Input" placeholder="00.000.000/0000-00" /></div>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.Phone label="Phone Input" /></div>
                                <div style={{ flex: 1 }}><Input.FullName label="Full Name Input" placeholder="John Doe" /></div>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.Date label="Date Input" /></div>
                                <div style={{ flex: 1 }}><Input.DateTime label="DateTime Input" /></div>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}><Input.Address label="Address Input" /></div>
                                <div style={{ flex: 1 }}>
                                    <Input.Select 
                                        label="Select Input" 
                                        value="" 
                                        options={[{label: 'Option 1', value: 1}, {label: 'Option 2', value: 2}]} 
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <Input.SelectOptions 
                                        label="Select Options Input" 
                                        options={[{label: 'Choice A', value: 'a'}, {label: 'Choice B', value: 'b'}]} 
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Input.Choices 
                                        label="Choices Input" 
                                        options={[{label: 'Choice 1', value: 1}, {label: 'Choice 2', value: 2}]} 
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <Input.AutoComplete 
                                        label="AutoComplete Input" 
                                        options={[{label: 'Result 1', value: 1}]} 
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <Input.Switch label="Switch Input Example" />
                            </div>
                        </div>

                        <PropDocTable rows={[
                            { name: 'label', type: 'string', defaultVal: 'undefined', description: 'Label text shown above the input fields.' },
                            { name: 'placeholder', type: 'string', defaultVal: 'undefined', description: 'Standard input placeholder.' },
                            { name: 'value', type: 'any', defaultVal: 'undefined', description: 'Controlled value.' },
                            { name: 'onInput / onChange', type: 'function', defaultVal: 'undefined', description: 'Handler receiving the value (signature depends on specific input variant).' },
                            { name: 'disabled', type: 'boolean', defaultVal: 'false', description: 'Disables interaction.' },
                            { name: 'start / end', type: 'ReactNode', defaultVal: 'null', description: 'Icons placed inside the input on left or right.' },
                            { name: 'options', type: 'Array<{label, value}>', defaultVal: '[]', description: 'Required for Select, SelectOptions, Choices, and AutoComplete variants.' }
                        ]} />
                    </div>
                );

            case 'StatusChip':
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="StatusChip Component" description="Displays a pill-shaped indicator for statuses using colors." />
                        
                        <div style={{ display: 'flex', gap: 16, padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                            <StatusChip.Success text="Success" />
                            <StatusChip.Error text="Error" />
                            <StatusChip.Alert text="Alert (Warning)" />
                            <StatusChip.Info text="Info" />
                            <StatusChip.Default text="Default" />
                            <StatusChip text="Custom Icon" icon={<CheckCircleRounded fontSize="small" />} variant="success" />
                        </div>

                        <PropDocTable rows={[
                            { name: 'text', type: 'string', defaultVal: 'undefined', description: 'Text inside the chip.' },
                            { name: 'variant', type: '"success" | "error" | "default" | "alert" | "info"', defaultVal: '"default"', description: 'Color scheme. Used directly or by using Subcomponents (e.g. StatusChip.Success).' },
                            { name: 'icon', type: 'ReactNode', defaultVal: 'null', description: 'Leading icon.' }
                        ]} />
                    </div>
                 );

            case 'InfoBlock':
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="InfoBlock Component" description="Used to present highlighted information or alerts within a layout." />
                        
                        <div style={{ padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                            <InfoBlock 
                                title="Important Notice" 
                                description="Your subscription will expire soon according to your plan settings." 
                                ctaText="Renew Plan"
                                ctaUrl="/billing"
                            />
                        </div>

                        <PropDocTable rows={[
                            { name: 'title', type: 'string', defaultVal: 'undefined', description: 'Main headline of the block.' },
                            { name: 'description', type: 'string', defaultVal: 'undefined', description: 'Subtext or detailed message.' },
                            { name: 'ctaText', type: 'string', defaultVal: 'undefined', description: 'Text for the call-to-action button (only shows if provided).' },
                            { name: 'ctaUrl', type: 'string', defaultVal: 'undefined', description: 'Link URL for the CTA button.' }
                        ]} />
                    </div>
                 );

            case 'Table':
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Table Component" description="Complete data table implementation supporting sorting, pagination, empty states, and custom rendering." />
                        
                        <div style={{ padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                            <Table 
                                id="example-table"
                                contained 
                                columns={sampleTableColumns} 
                                data={sampleTableData} 
                            />
                        </div>

                        <PropDocTable rows={[
                            { name: 'id', type: 'string', defaultVal: 'undefined', description: 'Unique key for column order localStorage persistence.' },
                            { name: 'columns', type: 'Array<object>', defaultVal: '[]', description: 'Configuration for columns: {title, field, render, customRender, align, width, grow}.' },
                            { name: 'data', type: 'Array<object>', defaultVal: '[]', description: 'Data rows.' },
                            { name: 'loading / loadingRows', type: 'boolean / number', defaultVal: 'false / 4', description: 'Displays skeleton states instead of data.' },
                            { name: 'contained / fullWidth', type: 'boolean', defaultVal: 'false', description: 'Layout modifiers: adds borders or removes padding.' },
                            { name: 'draggable', type: 'boolean', defaultVal: 'true', description: 'Allows users to reorder columns.' },
                            { name: 'enablePagination', type: 'boolean', defaultVal: 'false', description: 'Shows pagination bar below.' },
                            { name: 'onRowClick', type: 'function', defaultVal: 'undefined', description: '(rowData) => {...} click handler for table rows.' }
                        ]} />
                    </div>
                 );

            case 'Accordion':
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Accordion Component" description="Multi-step panels tailored for onboarding step completion." />
                        
                        <div style={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <Accordion 
                                title="Step 1: Introduction" 
                                summary="Basic information"
                                stepTitle="Tell us about yourself"
                                stepDescription="We just need a few details to get started."
                                isOpen={openStep === 'step1'}
                                isCompleted={completedSteps.step1}
                                onToggle={() => setOpenStep(openStep === 'step1' ? null : 'step1')}
                                onNext={() => { setCompletedSteps(p => ({...p, step1: true})); setOpenStep('step2'); }}
                            >
                                <Input.Text label="Full Name" placeholder="John Doe" />
                            </Accordion>

                            <Accordion 
                                title="Step 2: Preferences" 
                                stepTitle="Setup your account preferences"
                                isOpen={openStep === 'step2'}
                                isCompleted={completedSteps.step2}
                                onToggle={() => setOpenStep(openStep === 'step2' ? null : 'step2')}
                                onNext={() => { setCompletedSteps(p => ({...p, step2: true})); setOpenStep(null); }}
                            >
                                <Input.Switch label="Enable notifications" />
                            </Accordion>
                        </div>

                        <PropDocTable rows={[
                            { name: 'title / summary', type: 'string', defaultVal: 'undefined', description: 'Main title of the accordion header and brief completed sub-title.' },
                            { name: 'stepTitle / stepDescription', type: 'string', defaultVal: 'undefined', description: 'Title and description inside the opened content body.' },
                            { name: 'isOpen', type: 'boolean', defaultVal: 'undefined', description: 'Controls if the accordion body is visible.' },
                            { name: 'isCompleted', type: 'boolean', defaultVal: 'undefined', description: 'Displays a green checkmark icon in the header.' },
                            { name: 'onToggle', type: 'function', defaultVal: 'undefined', description: 'Callback when the header is clicked.' },
                            { name: 'onNext', type: 'function', defaultVal: 'undefined', description: 'Render a Concluir button that triggers this validation/state function.' },
                            { name: 'loading / disabled', type: 'boolean', defaultVal: 'false', description: 'Disables interaction or shows a spinner.' }
                        ]} />
                    </div>
                 );

            case 'Popper':
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <SectionHeader title="Popper Component" description="Floating dropdown menus controlled imperatively via refs. Fully supports single/multiple selection and search filtering." />
                        
                        <div style={{ display: 'flex', gap: 16, padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12, flexWrap: 'wrap' }}>
                            <div>
                                <Button 
                                    onClick={handleOpenPopperStandard}
                                    color="primary"
                                >
                                    Standard Menu
                                </Button>
                                <Popper controlRef={popperControlStandard} config={{ multiple: false }} />
                            </div>

                            <div>
                                <Button 
                                    onClick={handleOpenPopperSearch}
                                    color="white" variant="dashed"
                                >
                                    Searchable Menu
                                </Button>
                                <Popper 
                                    controlRef={popperControlSearch} 
                                    config={{ 
                                        multiple: false, 
                                        searchKeyExtractor: (opt, validate) => validate(opt.label)
                                    }} 
                                />
                            </div>

                            <div>
                                <Button 
                                    onClick={handleOpenPopperMulti}
                                    color="white" variant="text"
                                >
                                    Multiple Selection
                                </Button>
                                <Popper controlRef={popperControlMulti} config={{ multiple: true }} />
                            </div>
                        </div>

                        <PropDocTable rows={[
                            { name: 'controlRef', type: 'useRef()', defaultVal: 'undefined', description: 'Ref that gets assigned via useImperativeHandle yielding .open() and .close()' },
                            { name: 'config.multiple', type: 'boolean', defaultVal: 'false', description: 'Enables checkbox styling and internal state. Confirmation fires onClick handlers of items whose checked state changed.' },
                            { name: 'config.searchKeyExtractor', type: 'function', defaultVal: 'null', description: '(option, validate) => validate(option.label) - Filters results using an integrated search input.' },
                            { name: 'config.disableFlip', type: 'boolean', defaultVal: 'false', description: 'Prevents the popper from flipping to the top when hitting the bottom viewport boundary.' },
                        ]} />
                        
                        <Text.h4 style={{ marginTop: 16 }}>controlRef API</Text.h4>
                        <Text.p contrast={8} style={{ fontFamily: 'monospace', padding: 12, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
{`ref.current.open(optionsArray, 'bottom' | 'top', anchorElementRef.current)

Options Array Structure:
[ 
  { 
    id: string|number, 
    label: string, 
    icon: ReactNode, // Custom MUI icon
    variant: 'default' | 'danger' | 'warning',
    onClick: func, // Evaluates asynchronously with a builtin loader
    disabled: bool,
    checked: bool // Initial state used ONLY when config.multiple=true
  }
]`}
                        </Text.p>
                    </div>
                 );
            
            case 'Confirm':
                return (
                   <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                       <SectionHeader title="Confirm Component" description="Global confirmation modal invoked via window object." />
                       
                       <div style={{ padding: 24, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 12 }}>
                           <Button 
                               color="error"
                               onClick={() => {
                                   window.openConfirm({
                                       title: 'Delete Action',
                                       headline: 'Are you sure?',
                                       subtitle: 'This will irreversibly delete the item.',
                                       buttons: [
                                           { text: 'Confirm', color: 'error', variant: 'contained', onClick: () => alert('Deleted!') },
                                           { text: 'Cancel', variant: 'text' }
                                       ]
                                   })
                               }}
                           >
                               Trigger Confirm Modal
                           </Button>
                       </div>

                       <Text.p contrast={8} style={{ marginTop: 16 }}>
                           This component automatically handles loading state inside the 'onClick' handlers you pass to it, blocking inputs until resolution. It is typically rendered once in the App.js tree.
                       </Text.p>

                       <PropDocTable rows={[
                           { name: 'window.openConfirm({ config })', type: 'global function', defaultVal: '-', description: 'Method to launch the modal from anywhere.' },
                           { name: 'title', type: 'string', defaultVal: 'undefined', description: 'Header text of the modal.' },
                           { name: 'headline', type: 'string', defaultVal: 'undefined', description: 'Big question text (e.g. "Are you sure?").' },
                           { name: 'subtitle', type: 'string', defaultVal: 'undefined', description: 'Secondary context text.' },
                           { name: 'buttons', type: 'Array<Object>', defaultVal: '-', description: 'Definitions for buttons: [{ text, className, color, variant, onClick }]' }
                       ]} />
                   </div>
                );

             default:
                 return (
                     <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <Text.h2>{activeComponent}</Text.h2>
                        <Text.p>Component documentation is pending.</Text.p>
                     </div>
                 )
        }
    };

    return (
        <div style={{ display: 'flex', height: '100vh', width: '100%', }}>
            <div style={{ 
                width: '300px', 
                borderRight: '1px solid var(--border-color, rgba(255, 255, 255, 0.05))', 
                padding: '24px', 
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16
            }}>
                <Text.h3>Fragments Library</Text.h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: 16 }}>
                    {componentsList.map(comp => (
                        <Button
                            key={comp}
                            fullWidth
                            color={activeComponent === comp ? "overlay" : "none"}
                            onClick={() => setActiveComponent(comp)}
                            style={{ width: '100%', justifyContent: 'flex-start' }}
                        >
                            {comp}
                        </Button>
                    ))}
                </div>
            </div>
            <div style={{ 
                flex: 1, 
                padding: '48px', 
                overflowY: 'auto', 
            }}>
                <div style={{ maxWidth: 900, paddingBottom: 64 }}>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}
